import { Color } from '@persona-id/design-tokens';
import styled, { keyframes } from 'styled-components';

import IdBarcodeGenericGuide from 'images/pictographs/barcode-generic-guide.svg';
import IdBackGuide from 'inquiry/components/ScannerOverlay/IdBackGuide';
import IdBarcodeGuide from 'inquiry/components/ScannerOverlay/IdBarcodeGuide';
import IdCornersOnlyGuide from 'inquiry/components/ScannerOverlay/IdCornersOnlyGuide';
import IdFrontGuide from 'inquiry/components/ScannerOverlay/IdFrontGuide';
import IdFrontRightPortraitGuide from 'inquiry/components/ScannerOverlay/IdFrontRightPortraitGuide';
import MrzTwoLineGuide from 'inquiry/components/ScannerOverlay/MrzTwoLineGuide';
import { ClientSideOverlay, GovernmentIdOverlay } from 'lib/interfaces/verificationConfig';

import CustomAssetWithFallback from '../CustomAssetWithFallback';

interface OverlayImageProps {
  captureOverlay: GovernmentIdOverlay | null;
  hideGuide?: boolean;
  highlightCorners: boolean;
  isShortBarcodeOverlay?: boolean;
  showFlash?: boolean;
}

const flash = keyframes`
  0% {
    opacity: 100;
  }
  100% {
    opacity: 0;
  }
`;

const Flash = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: ${Color.PrimaryWhite};
  opacity: 0;
  animation: ${flash} 1s;
`;

const Positioner = styled('div')`
  transform: translate(-50%, -50%);
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;

  & > div,
  & > img {
    margin: auto;
  }
`;

const SimpleGuide = styled('div')`
  padding-bottom: 65.97%;
  width: 100%;
`;

const BarcodeImageContainer = styled('div')`
  position: absolute;
  margin-left: 20%;
  margin-right: 20%;
  padding-top: 17%;
  width: 60%;
  top: 50%;
  transform: translateY(-50%);
`;

const IdBarcodeGenericGuideStyled = styled(IdBarcodeGenericGuide)`
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
`;

const OverlayImage = (props: OverlayImageProps) => {
  const { captureOverlay, hideGuide, highlightCorners, isShortBarcodeOverlay, showFlash } = props;

  const renderOverlayFallback = () => {
    switch (captureOverlay?.['overlay-fallback']) {
      case ClientSideOverlay.Passport:
        return <MrzTwoLineGuide hideGuide={hideGuide} />;
      case ClientSideOverlay.IdFront:
        return <IdFrontGuide hideGuide={hideGuide} highlightCorners={highlightCorners} />;
      case ClientSideOverlay.IdFrontRightPortrait:
        return (
          <IdFrontRightPortraitGuide hideGuide={hideGuide} highlightCorners={highlightCorners} />
        );
      case ClientSideOverlay.IdBack:
        return <IdBackGuide hideGuide={hideGuide} highlightCorners={highlightCorners} />;
      case ClientSideOverlay.Barcode:
        if (isShortBarcodeOverlay) {
          // True if current side is pdf417
          return <IdBarcodeGuide />;
        } else {
          // This is a full-height capture window, because we want the user to
          // capture the full back. It is used when we are capturing back but
          // configure dynamic gov ID to show the barcode overlay. This is the
          // default config when country code is US and current side is back.
          // @see Inquiry::Step::DynamicFlow::GovernmentId::IdClasses#get_default_overlay_for_side
          return (
            <SimpleGuide>
              <BarcodeImageContainer>
                <IdBarcodeGenericGuideStyled preserveAspectRatio='none' />
              </BarcodeImageContainer>
            </SimpleGuide>
          );
        }
      case ClientSideOverlay.CornersOnly:
        return <IdCornersOnlyGuide highlightCorners={highlightCorners} />;
      case ClientSideOverlay.Empty:
      default:
        return <SimpleGuide />;
    }
  };

  return (
    <>
      <Positioner>
        <CustomAssetWithFallback customImage={captureOverlay?.['overlay']} alt='ID capture overlay'>
          {renderOverlayFallback()}
        </CustomAssetWithFallback>
      </Positioner>
      {showFlash && <Flash />}
    </>
  );
};

export default OverlayImage;
